import moment from 'moment'
import { IProcess, IProcessService } from './use-projects'
import { GroupedProcesses, Service } from './types'
import { STEP_MAPPING, STEP_ORDER } from './constants'

interface TranslationFunction {
  (key: string, options?: Record<string, unknown>): string
}

export const groupProcessesByStep = (processes: IProcess[]): GroupedProcesses => {
  return processes.reduce((acc: GroupedProcesses, process: IProcess) => {
    const mappedKind = STEP_MAPPING[process.kind]
    if (mappedKind) {
      if (!acc[mappedKind]) {
        acc[mappedKind] = []
      }
      acc[mappedKind].push(process)
    }
    return acc
  }, {})
}

export const filterProcessesWithDates = (processes: IProcess[]): IProcess[] => {
  return processes.filter(process => 
    process.start_at || process.end_at || process.private_start_at
  )
}

export const sortProcessesByDate = (processes: IProcess[]): IProcess[] => {
  return processes.sort((a, b) => {
    const dateA = new Date(a.start_at || a.private_start_at || '').getTime()
    const dateB = new Date(b.start_at || b.private_start_at || '').getTime()
    return dateA - dateB
  })
}

export const filterDuplicateServices = (services: Service[]): Service[] => {
  const serviceMap: { [key: string]: Service } = {}
  
  services.forEach(service => {
    if (!service.commissioned_at) {
      serviceMap[`no-date-${service.name}-${service.category}-${service.state}`] = service
      return
    }
    
    const dateKey = moment(service.commissioned_at).format('YYYY-MM-DD')
    const key = `${service.name}-${service.category}-${service.state}-${dateKey}`
    
    const existingService = serviceMap[key]
    if (!existingService || 
        moment(service.commissioned_at).isAfter(moment(existingService.commissioned_at))) {
      serviceMap[key] = service
    }
  })
  
  return Object.values(serviceMap)
}

export const filterDuplicateProcesses = (processes: IProcess[]): IProcess[] => {
  const seen: { [key: string]: boolean } = {}
  
  return processes.filter(process => {
    const dateKey = process.start_at || process.private_start_at
      ? moment(process.start_at || process.private_start_at).format('YYYY-MM-DD')
      : 'no-date'
    
    const servicesKey = process.services
      .map(s => `${s.name}-${s.category}`)
      .sort()
      .join('|')
    
    const key = `${process.kind}-${dateKey}-${servicesKey}`
    
    if (seen[key]) {
      return false
    }
    
    seen[key] = true
    return true
  })
}

export const filterServicesFromProcesses = (processes: IProcess[], projectServices: Service[]): IProcess[] => {
  return processes.map(process => {
    if (process.kind === 'procube_commissioning_process') {
      const filteredServices = process.services.filter(processService => {
        return projectServices.some(projectService => 
          projectService.name === processService.name &&
          projectService.category === processService.category &&
          projectService.commissioned_at
        )
      })
      
      if (filteredServices.length > 0) {
        return {
          ...process,
          services: filteredServices
        }
      }
      return null
    }
    
    return process
  }).filter((process): process is IProcess => process !== null)
}

export const processAndFilterData = (data: IProcess[], projectServices: Service[] = []): GroupedProcesses => {
  const processesWithValidServices = filterServicesFromProcesses(data, projectServices)
  const uniqueProcesses = filterDuplicateProcesses(processesWithValidServices)
  const grouped = groupProcessesByStep(uniqueProcesses)
  const filteredAndSorted: GroupedProcesses = {}
  
  STEP_ORDER.forEach(step => {
    if (grouped[step]?.length > 0) {
      const processesWithDates = filterProcessesWithDates(grouped[step])
      
      if (processesWithDates.length > 0) {
        filteredAndSorted[step] = sortProcessesByDate(processesWithDates)
      }
    }
  })

  return filteredAndSorted
}

export const getProcessDates = (process: IProcess): string[] => {
  if (!process.start_at && !process.end_at) return []
  
  const startDate = process.start_at
  const endDate = process.end_at
  
  if (startDate && endDate && moment(startDate).format('YYYYMMDD') === moment(endDate).format('YYYYMMDD')) {
    return [startDate]
  }
  
  if (startDate && !endDate) return [startDate]
  
  return [startDate, endDate].filter((date): date is string => Boolean(date))
}

const getDateStatus = (date: string, now: moment.Moment): string | null => {
  const startDate = moment(date)
  const daysDiff = startDate.diff(now, 'days')
  if (daysDiff >= 0 && daysDiff < 7) {
    return 'scheduled_soon'
  }
  if (daysDiff >= 7) {
    return 'upcoming'
  }
  return null
}

export const getProcessStatus = (process: IProcess): string | null => {
  const now = moment()
  
  if (process.status === 'ended') {
    return 'completed'
  }

  if (process.end_at && now.isAfter(moment(process.end_at))) {
    return 'completed'
  }

  if (process.status === 'running') {
    return 'in_progress'
  }

  if (process.start_at && now.isAfter(moment(process.start_at))) {
    if (!process.end_at || now.isBefore(moment(process.end_at))) {
      return 'in_progress'
    }
  }

  if (process.private_start_at) {
    return getDateStatus(process.private_start_at, now)
  }

  if (process.start_at && !process.status) {
    return getDateStatus(process.start_at, now)
  }

  if (process.start_at && now.isBefore(moment(process.start_at))) {
    return 'upcoming'
  }

  return null
}

export const getUniqueCategories = (services: IProcessService[]): string[] => {
  return services.reduce<string[]>((acc, item) => 
    acc.includes(item.category) ? acc : [...acc, item.category], [])
}

export const formatDateRange = (dates: string[], t: TranslationFunction): string | null => {
  if (dates.length === 0) return null
  
  if (dates.length === 1) {
    const formattedDate = moment(dates[0]).format('DD/MM/YYYY')
    return t('projects.process.date.realized_on', { date: formattedDate })
  }
  
  const startDate = moment(dates[0]).format('DD/MM/YYYY')
  const endDate = moment(dates[1]).format('DD/MM/YYYY')
  
  return t('projects.process.date.completed_from_to', { startDate, endDate })
}

export const formatInProgressDate = (process: IProcess, t: TranslationFunction): string | null => {
  if (!process.start_at) return null
  
  const startDate = moment(process.start_at).format('DD/MM/YYYY')
  return t('projects.process.date.started_on', { date: startDate })
}

export const areAllProcessesInStepCompleted = (processes: IProcess[]): boolean => {
  return processes.every(process => {
    const now = moment()
    return process.status === 'ended' || (process.end_at && now.isAfter(moment(process.end_at)))
  })
}

export const shouldShowServiceInCommissioning = (service: Service, commissioningProcesses: IProcess[]): boolean => {
  if (!service.commissioned_at || !service.category) return false
  
  const serviceCategories = commissioningProcesses
    .flatMap(process => process.services.map(s => s.category))
    .filter((category): category is string => Boolean(category))
  
  return serviceCategories.includes(service.category)
}

export const getLatestDeliveredServiceDate = (services: Service[], processData: IProcess[]): string | null => {
  const servicesInProgress = services.some(service => service.state === 'in_progress')
  if (servicesInProgress) {
    return null
  }

  const serviceDates = services
    .filter(service => service.commissioned_at && service.state !== 'in_progress')
    .map(service => service.commissioned_at)
    .filter((date): date is string => Boolean(date))

  const mesProcessDates = processData
    .filter(process => process.kind === 'procube_commissioning_process')
    .flatMap(process => {
      const dates: string[] = []
      if (process.start_at) dates.push(process.start_at)
      if (process.end_at) dates.push(process.end_at)
      return dates
    })

  const allDates = [...serviceDates, ...mesProcessDates]
  const uniqueDates = [...new Set(allDates)]

  if (uniqueDates.length === 0) return null

  return uniqueDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]
}
